import { post } from "./request";

const VETO_INIT_PATH =
  process.env.REACT_APP_VETO_INIT_PATH ||
  process.env.REACT_APP_VETO_SAVE_PATH ||
  "/social/veto/init";

export const POLL_ERROR_CODES = {
  QUESTION_REQUIRED: "QUESTION_REQUIRED",
  OPTIONS_REQUIRED: "OPTIONS_REQUIRED",
  MAX_SELECTED_INVALID: "MAX_SELECTED_INVALID",
  CREATE_POLL_FAILED: "CREATE_POLL_FAILED",
  POLL_ID_MISSING: "POLL_ID_MISSING",
};

export const unwrapVetoInitId = res => {
  const d = res?.data;
  if (d == null || d === "") return null;
  if (typeof d === "number" && Number.isFinite(d)) return d;
  if (typeof d === "string" && String(d).trim() !== "") {
    const n = Number(d);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof d === "object" && d) {
    const id = d.id ?? d.voteId ?? d.vetoId ?? d.headerId;
    if (id != null && id !== "") {
      const n = Number(id);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
};

export const getFilledPollOptions = options =>
  (options || []).map(s => String(s).trim()).filter(Boolean);

export const validatePollDraft = ({
  title,
  options,
  choiceType,
  maxSelected,
}) => {
  const pollTitle = String(title || "").trim();
  const filledOptions = getFilledPollOptions(options);
  if (!pollTitle) {
    return { ok: false, code: POLL_ERROR_CODES.QUESTION_REQUIRED };
  }
  if (filledOptions.length < 2) {
    return { ok: false, code: POLL_ERROR_CODES.OPTIONS_REQUIRED };
  }
  const normalizedChoiceType = choiceType === "MULTI" ? "MULTI" : "SINGLE";
  if (normalizedChoiceType === "MULTI") {
    const maxSel = Number(maxSelected);
    if (
      !Number.isFinite(maxSel) ||
      maxSel < 2 ||
      maxSel > filledOptions.length
    ) {
      return {
        ok: false,
        code: POLL_ERROR_CODES.MAX_SELECTED_INVALID,
        optionsCount: filledOptions.length,
      };
    }
  }
  return {
    ok: true,
    title: pollTitle,
    options: filledOptions,
    choiceType: normalizedChoiceType,
    maxSelected:
      normalizedChoiceType === "MULTI"
        ? Math.min(
            Math.max(2, Number(maxSelected) || 2),
            filledOptions.length,
          )
        : 1,
  };
};

export const createPollAndGetVoteId = async ({
  title,
  options,
  choiceType,
  maxSelected,
  parentId,
  parentType,
}) => {
  const validated = validatePollDraft({
    title,
    options,
    choiceType,
    maxSelected,
  });
  if (!validated.ok) {
    const error = new Error(validated.code);
    error.code = validated.code;
    error.optionsCount = validated.optionsCount;
    throw error;
  }

  const vetoRes = await post(VETO_INIT_PATH, {
    choiceType: validated.choiceType === "MULTI" ? "CHECK_BOX" : "RADIO",
    id: 0,
    maxSelectedCnt: validated.maxSelected,
    options: validated.options.map(name => ({
      icon: "",
      name,
      optionId: 0,
    })),
    parentId: Number(parentId) || 0,
    parentType,
    title: validated.title,
  });

  const vetoOk =
    vetoRes?.success === true ||
    vetoRes?.code === 0 ||
    (vetoRes?.data != null && vetoRes?.data !== false);
  if (!vetoOk) {
    const error = new Error(vetoRes?.message || "Failed to create poll");
    error.code = POLL_ERROR_CODES.CREATE_POLL_FAILED;
    throw error;
  }

  const voteId = unwrapVetoInitId(vetoRes);
  if (voteId == null) {
    const error = new Error("Poll created but server did not return a vote id");
    error.code = POLL_ERROR_CODES.POLL_ID_MISSING;
    throw error;
  }

  return voteId;
};

export const getPollErrorMessage = (error, t, messageKeys = {}) => {
  const keys = {
    questionRequired: messageKeys.questionRequired || "topic.warn.pollQuestion",
    questionRequiredFallback:
      messageKeys.questionRequiredFallback || "Please enter a poll question",
    optionsRequired: messageKeys.optionsRequired || "topic.warn.pollOptions",
    optionsRequiredFallback:
      messageKeys.optionsRequiredFallback ||
      "Please enter at least two poll options",
    maxSelectedInvalid:
      messageKeys.maxSelectedInvalid || "comment.poll.maxSelectedRange",
    maxSelectedInvalidFallback:
      messageKeys.maxSelectedInvalidFallback ||
      "Max selections must be between 2 and {count}",
    pollIdMissing: messageKeys.pollIdMissing || "topic.error.pollNoId",
    pollIdMissingFallback:
      messageKeys.pollIdMissingFallback ||
      "Poll created but server did not return a vote id",
    createPollFailed: messageKeys.createPollFailed || "topic.error.createPoll",
    createPollFailedFallback:
      messageKeys.createPollFailedFallback || "Failed to create poll",
  };

  if (error?.code === POLL_ERROR_CODES.QUESTION_REQUIRED) {
    return t(keys.questionRequired, keys.questionRequiredFallback);
  }
  if (error?.code === POLL_ERROR_CODES.OPTIONS_REQUIRED) {
    return t(keys.optionsRequired, keys.optionsRequiredFallback);
  }
  if (error?.code === POLL_ERROR_CODES.MAX_SELECTED_INVALID) {
    return t(keys.maxSelectedInvalid, keys.maxSelectedInvalidFallback, {
      count: error?.optionsCount || 2,
    });
  }
  if (error?.code === POLL_ERROR_CODES.POLL_ID_MISSING) {
    return t(keys.pollIdMissing, keys.pollIdMissingFallback);
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }
  return t(keys.createPollFailed, keys.createPollFailedFallback);
};

